import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getOptionChain, getOptionExpirations, placeOptionOrder } from '../api/options'
import { getPortfolio, getQuote } from '../api/trading'
import { previewFleet, submitFleet } from '../api/fleet'
import { ApiError } from '../api/http'
import { useAuthStore } from '../auth/useAuthStore'
import { useLiveQuote } from '../hooks/useLiveQuote'
import { useSetting } from '../hooks/useSetting'
import { usePortfolioSummary } from '../hooks/usePortfolioSummary'
import { useDailyCloseLimit } from '../hooks/useDailyCloseLimit'
import { useOpeningCutoff } from '../hooks/useOpeningCutoff'
import { useMaxLossLimit } from '../hooks/useMaxLossLimit'
import { useMaxGainLimit } from '../hooks/useMaxGainLimit'
import { useOrbBlock } from '../hooks/useOrbBlock'
import { useTrendBlock } from '../hooks/useTrendBlock'
import { composeBuyDisabledMessage } from '../lib/buyDisabled'
import { toast } from '../toast/useToastStore'
import { resolveLimitPrice, roundToCent, type OrderTypeMode } from '../lib/limitPrice'
import { useIsMarketOpen, isPastCutoffET, todayET, OPTIONS_CUTOFF_KEY } from '../lib/marketHours'
import type {
  AccountSummary,
  FleetSubmitResult,
  OptionChainContract,
  OptionInstrument,
  OptionSide,
  OrderIntent,
  PortfolioResponse,
  PreflightResponse,
} from '../api/types'
import { OptionsChainTable, type ChainRow } from './OptionsChainTable'
import { PreflightGrid } from './fleet/PreflightGrid'
import { StatusGrid } from './fleet/StatusGrid'

type OptionTradeMode = 'CONTRACTS' | 'PERCENT' | 'DOLLARS'

function fmtMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function contractPriceUSD(c: OptionChainContract): number | null {
  const ask = Number(c.ask)
  if (Number.isFinite(ask) && ask > 0) return ask * 100
  const mid = Number(c.optionDetails?.midPrice)
  if (Number.isFinite(mid) && mid > 0) return mid * 100
  const last = Number(c.last)
  if (Number.isFinite(last) && last > 0) return last * 100
  return null
}

interface Props {
  symbol: string
  /** Chart-panel layout: controls collapse into a popover; Up/Down buttons fill the panel. */
  dense?: boolean
}

const ARM_TIMEOUT_MS = 3000

function formatStrike(v: string | undefined): string {
  if (!v) return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return v
  return n.toFixed(2)
}

function formatExpiration(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit', timeZone: 'UTC' })
}

function computeOpenClose(
  action: 'BUY' | 'SELL',
  optionSymbol: string,
  portfolio: PortfolioResponse | undefined,
): 'OPEN' | 'CLOSE' {
  const pos = portfolio?.positions.find(
    (p) => p.instrument.type === 'OPTION' && p.instrument.symbol === optionSymbol,
  )
  if (!pos) return 'OPEN'
  const qty = Number(pos.quantity)
  if (!Number.isFinite(qty) || qty === 0) return 'OPEN'
  if (qty > 0) return action === 'BUY' ? 'OPEN' : 'CLOSE'
  return action === 'BUY' ? 'CLOSE' : 'OPEN'
}

function buildRows(
  calls: OptionChainContract[] | undefined,
  puts: OptionChainContract[] | undefined,
  underlying: number | null,
  strikesEach: number,
): { rows: ChainRow[]; atmStrike: number | null } {
  const byStrike = new Map<number, ChainRow>()
  const ingest = (list: OptionChainContract[] | undefined, key: 'call' | 'put') => {
    if (!list) return
    for (const c of list) {
      const strike = Number(c.optionDetails?.strikePrice)
      if (!Number.isFinite(strike)) continue
      let row = byStrike.get(strike)
      if (!row) {
        row = { strike, strikeText: strike.toFixed(2) }
        byStrike.set(strike, row)
      }
      row[key] = c
    }
  }
  ingest(calls, 'call')
  ingest(puts, 'put')

  const all = Array.from(byStrike.values()).sort((a, b) => a.strike - b.strike)
  if (all.length === 0) return { rows: [], atmStrike: null }

  let atmIdx = 0
  if (underlying != null && Number.isFinite(underlying)) {
    let bestDist = Infinity
    all.forEach((r, i) => {
      const d = Math.abs(r.strike - underlying)
      if (d < bestDist) {
        bestDist = d
        atmIdx = i
      }
    })
  } else {
    atmIdx = Math.floor(all.length / 2)
  }

  const start = Math.max(0, atmIdx - strikesEach)
  const end = Math.min(all.length, atmIdx + strikesEach + 1)
  return { rows: all.slice(start, end), atmStrike: all[atmIdx].strike }
}

function findAtmStrike(
  calls: OptionChainContract[] | undefined,
  puts: OptionChainContract[] | undefined,
  underlying: number | null,
): number | null {
  const strikes = new Set<number>()
  for (const list of [calls, puts]) {
    if (!list) continue
    for (const c of list) {
      const s = Number(c.optionDetails?.strikePrice)
      if (Number.isFinite(s)) strikes.add(s)
    }
  }
  if (strikes.size === 0) return null
  const all = Array.from(strikes).sort((a, b) => a - b)
  if (underlying == null || !Number.isFinite(underlying)) {
    return all[Math.floor(all.length / 2)]
  }
  let best = all[0]
  let bestDist = Math.abs(best - underlying)
  for (const s of all) {
    const d = Math.abs(s - underlying)
    if (d < bestDist) {
      bestDist = d
      best = s
    }
  }
  return best
}

function optionLabelFor(
  baseSymbol: string,
  side: OptionSide,
  strike: string,
  expiration: string,
): string {
  return `${baseSymbol} ${formatExpiration(expiration)} ${formatStrike(strike)} ${side}`
}

export function OptionsSection({ symbol, dense = false }: Props) {
  const accountId = useAuthStore((s) => s.accountId)
  const accounts = useAuthStore((s) => s.accounts)
  const armedCount = useMemo(() => accounts.filter((a) => a.armed).length, [accounts])
  const isFleetMode = armedCount >= 2
  const qc = useQueryClient()
  const closeLimit = useDailyCloseLimit()
  const openingCutoff = useOpeningCutoff()
  const maxLoss = useMaxLossLimit()
  const maxGain = useMaxGainLimit()
  const marketOpen = useIsMarketOpen()

  const expQ = useQuery({
    queryKey: ['option-expirations', symbol, accountId],
    queryFn: () => getOptionExpirations(symbol),
    enabled: !!accountId && !!symbol,
    staleTime: 5 * 60_000,
  })

  const [searchParams, setSearchParams] = useSearchParams()

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(updates)) {
          if (v == null || v === '') next.delete(k)
          else next.set(k, v)
        }
        return next
      },
      { replace: true },
    )
  }

  const expirations = expQ.data?.expirations ?? []
  const expFromUrl = searchParams.get('exp')
  const [cutoffTime] = useSetting<string | null>(OPTIONS_CUTOFF_KEY, null)
  const defaultExpiration: string | null = (() => {
    if (expirations.length === 0) return null
    const first = expirations[0]
    if (first === todayET() && isPastCutoffET(cutoffTime) && expirations.length > 1) {
      return expirations[1]
    }
    return first
  })()
  const expiration: string | null =
    expFromUrl && expirations.includes(expFromUrl)
      ? expFromUrl
      : defaultExpiration

  function setExpiration(v: string) {
    updateParams({ exp: v })
  }

  const chainQ = useQuery({
    queryKey: ['option-chain', symbol, expiration, accountId],
    queryFn: () => getOptionChain(symbol, expiration as string),
    enabled: !!accountId && !!symbol && !!expiration,
    refetchInterval: marketOpen ? 5000 : false,
    refetchIntervalInBackground: false,
  })

  const quoteQ = useQuery({
    queryKey: ['quote', symbol, accountId],
    queryFn: () => getQuote(symbol),
    enabled: !!accountId && !!symbol,
    refetchInterval: marketOpen ? 5000 : false,
    refetchIntervalInBackground: false,
  })

  const portfolioQ = useQuery({
    queryKey: ['portfolio', accountId],
    queryFn: getPortfolio,
    enabled: !!accountId,
    refetchInterval: 5000,
  })

  const underlying = useMemo(() => {
    const n = Number(quoteQ.data?.quotes?.[0]?.last)
    return Number.isFinite(n) ? n : null
  }, [quoteQ.data])

  const orbBlock = useOrbBlock(symbol, underlying)
  const trendBlock = useTrendBlock(symbol)
  const marketClosedReason = marketOpen ? null : 'Market closed (RTH 09:30–16:00 ET)'
  const sharedBlockReasons = [
    marketClosedReason,
    openingCutoff.reason,
    closeLimit.capReason,
    maxLoss.capReason,
    maxGain.capReason,
    orbBlock.reason,
  ]
  const callBuyBlockedMessage = composeBuyDisabledMessage([
    ...sharedBlockReasons,
    trendBlock.longReason,
  ])
  const putBuyBlockedMessage = composeBuyDisabledMessage([
    ...sharedBlockReasons,
    trendBlock.shortReason,
  ])

  const atmStrike = useMemo(
    () => findAtmStrike(chainQ.data?.calls, chainQ.data?.puts, underlying),
    [chainQ.data, underlying],
  )

  const atmContracts = useMemo(() => {
    if (atmStrike == null) return { call: null as OptionChainContract | null, put: null as OptionChainContract | null }
    const call =
      chainQ.data?.calls?.find((c) => Number(c.optionDetails?.strikePrice) === atmStrike) ?? null
    const put =
      chainQ.data?.puts?.find((c) => Number(c.optionDetails?.strikePrice) === atmStrike) ?? null
    return { call, put }
  }, [chainQ.data, atmStrike])

  const [quickEnabled] = useSetting<boolean>('quick-trade-enabled', true)
  const [orderMode] = useSetting<OrderTypeMode>('order-type-mode', 'MARKET')

  const [defaultQuickTradeMode] = useSetting<'SHARES' | 'PERCENT' | 'DOLLARS'>(
    'default-quick-trade-mode',
    'SHARES',
  )
  const initialOptMode: OptionTradeMode =
    defaultQuickTradeMode === 'PERCENT'
      ? 'PERCENT'
      : defaultQuickTradeMode === 'DOLLARS'
        ? 'DOLLARS'
        : 'CONTRACTS'
  const [optMode, setOptMode] = useSetting<OptionTradeMode>('option-trade-mode', initialOptMode)
  const [showChain, setShowChain] = useSetting<boolean>('option-show-chain', false)
  const [strikesEach, setStrikesEach] = useSetting<number>('option-strikes-each', 1)
  const [contractsQty, setContractsQty] = useSetting<string>('option-trade-qty', '1')
  const [optPct, setOptPct] = useSetting<string>('option-trade-pct', '5')
  const [optAmt, setOptAmt] = useSetting<string>('option-trade-amt', '500')
  const [armedKey, setArmedKey] = useState<string | null>(null)
  const armTimerRef = useRef<number | null>(null)
  const [submittingKey, setSubmittingKey] = useState<string | null>(null)

  // Dense (chart-panel) popover for the controls.
  const [controlsOpen, setControlsOpen] = useState(false)
  const controlsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!controlsOpen) return
    function onDown(e: MouseEvent) {
      if (controlsRef.current && !controlsRef.current.contains(e.target as Node)) setControlsOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [controlsOpen])

  // Fleet preflight payloads — built from ATM contracts when available.
  // referencePrice is the per-share premium (ask); server multiplies by 100 for options.
  // Sizing mirrors the user's selected option-sizing mode:
  //   CONTRACTS → SHARES with the typed contract count (clamped to >= 1)
  //   PERCENT   → PERCENT of each armed account's own equity
  //   DOLLARS   → DOLLARS per account (server divides by premium*100)
  const fleetSizing = useMemo<{ mode: 'SHARES' | 'PERCENT' | 'DOLLARS'; value: number }>(() => {
    if (optMode === 'PERCENT') return { mode: 'PERCENT', value: Number(optPct) || 0 }
    if (optMode === 'DOLLARS') return { mode: 'DOLLARS', value: Number(optAmt) || 0 }
    const n = Number(contractsQty)
    return { mode: 'SHARES', value: Number.isFinite(n) && n >= 1 ? Math.round(n) : 1 }
  }, [optMode, contractsQty, optPct, optAmt])

  const upFleetPayload = useMemo(() => {
    const c = atmContracts.call
    if (!c || !expiration) return null
    const px = Number(c.ask) > 0 ? Number(c.ask) : Number(c.last) > 0 ? Number(c.last) : 0
    return {
      intent: {
        symbol: c.instrument.symbol,
        instrumentType: 'OPTION' as const,
        side: 'BUY' as const,
        type: 'MARKET' as const,
        optionInstrument: { symbol: c.instrument.symbol, type: 'OPTION' as const },
      } as OrderIntent,
      sizing: fleetSizing,
      referencePrice: px,
    }
  }, [atmContracts.call, expiration, fleetSizing])

  const downFleetPayload = useMemo(() => {
    const p = atmContracts.put
    if (!p || !expiration) return null
    const px = Number(p.ask) > 0 ? Number(p.ask) : Number(p.last) > 0 ? Number(p.last) : 0
    return {
      intent: {
        symbol: p.instrument.symbol,
        instrumentType: 'OPTION' as const,
        side: 'BUY' as const,
        type: 'MARKET' as const,
        optionInstrument: { symbol: p.instrument.symbol, type: 'OPTION' as const },
      } as OrderIntent,
      sizing: fleetSizing,
      referencePrice: px,
    }
  }, [atmContracts.put, expiration, fleetSizing])

  const upPreflightQuery = useQuery({
    queryKey: ['fleet', 'preflight', upFleetPayload],
    queryFn: () => previewFleet(upFleetPayload!),
    enabled: isFleetMode && upFleetPayload != null && upFleetPayload.referencePrice > 0,
    staleTime: 1000,
    placeholderData: keepPreviousData,
  })

  const downPreflightQuery = useQuery({
    queryKey: ['fleet', 'preflight', downFleetPayload],
    queryFn: () => previewFleet(downFleetPayload!),
    enabled: isFleetMode && downFleetPayload != null && downFleetPayload.referencePrice > 0,
    staleTime: 1000,
    placeholderData: keepPreviousData,
  })

  const [lastUpResult, setLastUpResult] = useState<FleetSubmitResult | null>(null)
  const [lastDownResult, setLastDownResult] = useState<FleetSubmitResult | null>(null)

  // Auto-dismiss when the underlying payload changes (new ATM contract, new qty)
  useEffect(() => { setLastUpResult(null) }, [upFleetPayload])
  useEffect(() => { setLastDownResult(null) }, [downFleetPayload])

  const upFleetMutation = useMutation({
    mutationFn: () => submitFleet(upFleetPayload!),
    onSuccess: (result: FleetSubmitResult) => {
      setLastUpResult(result)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`Fleet Up (Call) order failed — ${msg}`)
    },
  })

  const downFleetMutation = useMutation({
    mutationFn: () => submitFleet(downFleetPayload!),
    onSuccess: (result: FleetSubmitResult) => {
      setLastDownResult(result)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`Fleet Down (Put) order failed — ${msg}`)
    },
  })

  const chainRows = useMemo(
    () =>
      showChain
        ? buildRows(chainQ.data?.calls, chainQ.data?.puts, underlying, strikesEach).rows
        : [],
    [showChain, chainQ.data, underlying, strikesEach],
  )

  // Live-stream the symbols actually rendered: every visible chain row when
  // the full chain is shown, ATM call+put otherwise, plus the underlying so
  // ATM tracking stays current. The useLiveQuote hook diffs adds/removes
  // against its previous tick, so changing the list during refetches stays
  // cheap.
  const liveSymbols = useMemo(() => {
    const set = new Set<string>()
    if (symbol) set.add(symbol)
    if (showChain) {
      for (const row of chainRows) {
        const c = row.call?.instrument.symbol
        const p = row.put?.instrument.symbol
        if (c) set.add(c)
        if (p) set.add(p)
      }
    } else {
      const c = atmContracts.call?.instrument.symbol
      const p = atmContracts.put?.instrument.symbol
      if (c) set.add(c)
      if (p) set.add(p)
    }
    return Array.from(set)
  }, [symbol, showChain, chainRows, atmContracts])

  useLiveQuote(liveSymbols)

  const summary = usePortfolioSummary()
  const totalEquity = summary.portfolioValue
  const optionsBp = summary.optionsBp

  const optInputValue =
    optMode === 'CONTRACTS' ? contractsQty : optMode === 'PERCENT' ? optPct : optAmt
  const setOptInputValue = (v: string) => {
    if (optMode === 'CONTRACTS') setContractsQty(v)
    else if (optMode === 'PERCENT') setOptPct(v)
    else setOptAmt(v)
  }

  const optParsed = Number(optInputValue)
  const optValid = Number.isFinite(optParsed) && optParsed > 0
  const dollarTarget =
    optMode === 'DOLLARS'
      ? optValid
        ? optParsed
        : null
      : optMode === 'PERCENT'
        ? optValid && totalEquity > 0
          ? (optParsed / 100) * totalEquity
          : null
        : null
  const exceedsOptionsBp =
    dollarTarget != null && optionsBp > 0 && dollarTarget > optionsBp

  function clearArm() {
    if (armTimerRef.current != null) {
      window.clearTimeout(armTimerRef.current)
      armTimerRef.current = null
    }
    setArmedKey(null)
  }

  useEffect(() => {
    clearArm()
  }, [symbol, expiration])

  useEffect(() => () => clearArm(), [])

  const mutation = useMutation({
    mutationFn: (params: {
      instrument: OptionInstrument
      action: 'BUY' | 'SELL'
      contracts: number
      openCloseIndicator: 'OPEN' | 'CLOSE'
      cellKey: string
      label: string
      mode: OrderTypeMode
      limitPrice?: number
    }) =>
      placeOptionOrder({
        instrument: params.instrument,
        side: params.action,
        contracts: params.contracts,
        openCloseIndicator: params.openCloseIndicator,
        mode: params.mode,
        limitPrice: params.limitPrice,
      }),
    onMutate: (params) => {
      setSubmittingKey(params.cellKey)
    },
    onSuccess: (resp, params) => {
      setSubmittingKey(null)
      const verb = params.action === 'BUY' ? 'Bought' : 'Sold'
      toast.success(`${verb} ${params.contracts}× ${params.label} · Order ${resp.orderId}`)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
    },
    onError: (err, params) => {
      setSubmittingKey(null)
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`${params.action} ${params.contracts}× ${params.label} failed — ${msg}`)
    },
  })

  function resolveContractsFor(
    contract: OptionChainContract,
    action: 'BUY' | 'SELL',
  ): { contracts: number; costUSD: number | null } | { error: string } {
    if (!showChain && optMode !== 'CONTRACTS') {
      if (dollarTarget == null || dollarTarget <= 0) {
        return {
          error:
            optMode === 'PERCENT'
              ? 'Need a positive % and account equity'
              : 'Need a positive $ amount',
        }
      }
      const pricePerContract = contractPriceUSD(contract)
      if (pricePerContract == null) return { error: 'No price available for this contract' }
      const contracts = Math.floor(dollarTarget / pricePerContract)
      if (contracts < 1) {
        return {
          error: `${fmtMoney(dollarTarget)} buys 0 contracts at ${fmtMoney(pricePerContract)} each`,
        }
      }
      const costUSD = contracts * pricePerContract
      if (action === 'BUY' && optionsBp > 0 && costUSD > optionsBp) {
        return {
          error: `${fmtMoney(costUSD)} exceeds options BP ${fmtMoney(optionsBp)}`,
        }
      }
      return { contracts, costUSD }
    }
    const n = Number(contractsQty)
    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
      return { error: 'Enter whole-contract qty > 0' }
    }
    const pricePerContract = contractPriceUSD(contract)
    const costUSD = pricePerContract != null ? n * pricePerContract : null
    return { contracts: n, costUSD }
  }

  function submit(side: OptionSide, action: 'BUY' | 'SELL', contract: OptionChainContract) {
    if (!expiration) return
    const result = resolveContractsFor(contract, action)
    if ('error' in result) {
      toast.error(`${action} ${symbol} ${side} failed — ${result.error}`)
      return
    }
    let limitPrice: number | undefined
    if (orderMode === 'LIMIT') {
      const px = resolveLimitPrice({
        side: action,
        bid: contract.bid,
        ask: contract.ask,
        last: contract.last,
      })
      if (px == null) {
        toast.error(`${action} ${symbol} ${side} failed — no live price for limit order`)
        return
      }
      limitPrice = roundToCent(px)
    }
    const optionSymbol = contract.instrument.symbol
    const instrument: OptionInstrument = {
      symbol: optionSymbol,
      type: 'OPTION',
    }
    const openCloseIndicator = computeOpenClose(action, optionSymbol, portfolioQ.data)
    const label = optionLabelFor(symbol, side, contract.optionDetails.strikePrice, expiration)
    const cellKey = `${optionSymbol}:${action}`
    clearArm()
    mutation.mutate({
      instrument,
      action,
      contracts: result.contracts,
      openCloseIndicator,
      cellKey,
      label,
      mode: orderMode,
      limitPrice,
    })
  }

  function handleCellClick(
    side: OptionSide,
    action: 'BUY' | 'SELL',
    contract: OptionChainContract,
  ) {
    if (submittingKey) return
    if (action === 'BUY') {
      const sideMsg = side === 'CALL' ? callBuyBlockedMessage : putBuyBlockedMessage
      if (sideMsg) return
    }
    // Fleet path: skip arm/confirm, fire fleet mutation immediately
    if (isFleetMode && action === 'BUY' && !showChain) {
      if (side === 'CALL') {
        if (upFleetPayload) upFleetMutation.mutate()
      } else {
        if (downFleetPayload) downFleetMutation.mutate()
      }
      return
    }
    // Single-account path — unchanged
    if (quickEnabled) {
      submit(side, action, contract)
      return
    }
    const key = `${contract.instrument.symbol}:${action}`
    if (armedKey === key) {
      submit(side, action, contract)
      return
    }
    setArmedKey(key)
    if (armTimerRef.current != null) window.clearTimeout(armTimerRef.current)
    armTimerRef.current = window.setTimeout(() => {
      setArmedKey(null)
      armTimerRef.current = null
    }, ARM_TIMEOUT_MS)
  }

  const noOptions = expQ.isSuccess && expirations.length === 0

  const sizeSummary =
    optMode === 'CONTRACTS' ? `${contractsQty}×` : optMode === 'PERCENT' ? `${optPct}%` : `$${optAmt}`
  const triggerSummary = `${expiration ? formatExpiration(expiration) : '—'} · ${sizeSummary}${
    showChain ? ' · chain' : ''
  }`

  const controlsInner = (
    <>
      <label className="flex items-center gap-2 text-xs text-zinc-400">
        <span>Expiration</span>
        <select
          value={expiration ?? ''}
          onChange={(e) => setExpiration(e.target.value)}
          className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-sm font-mono focus:outline-none focus:border-emerald-500"
        >
          {expirations.map((e) => (
            <option key={e} value={e}>
              {formatExpiration(e)}
            </option>
          ))}
        </select>
      </label>

      {!showChain ? (
        <>
          <div
            role="radiogroup"
            aria-label="Option size mode"
            className="inline-flex rounded border border-zinc-700 overflow-hidden text-sm"
          >
            {(['CONTRACTS', 'PERCENT', 'DOLLARS'] as const).map((m) => {
              const label = m === 'CONTRACTS' ? 'Contracts' : m === 'PERCENT' ? '%' : '$'
              const selected = optMode === m
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setOptMode(m)
                    clearArm()
                  }}
                  className={`px-3 py-1.5 font-mono leading-5 transition ${
                    selected
                      ? 'bg-emerald-600 text-white'
                      : 'bg-zinc-950 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            <input
              type="number"
              min={optMode === 'CONTRACTS' ? 1 : 0}
              step={optMode === 'CONTRACTS' ? 1 : optMode === 'PERCENT' ? 1 : 0.01}
              aria-label={
                optMode === 'CONTRACTS'
                  ? 'Contracts'
                  : optMode === 'PERCENT'
                    ? 'Percent of equity'
                    : 'Dollar amount'
              }
              value={optInputValue}
              onChange={(e) => {
                setOptInputValue(e.target.value)
                clearArm()
              }}
              className="w-24 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 font-mono text-sm text-center focus:outline-none focus:border-emerald-500"
            />
            {optMode === 'PERCENT' && <span className="font-mono text-zinc-500">%</span>}
            {optMode === 'DOLLARS' && <span className="font-mono text-zinc-500">USD</span>}
          </label>
          {optMode === 'PERCENT' && (
            <div className="text-xs text-zinc-500">
              of equity{' '}
              <span className="font-mono text-zinc-300">{fmtMoney(totalEquity)}</span>
              {dollarTarget != null && (
                <>
                  {' '}
                  ={' '}
                  <span
                    className={`font-mono ${exceedsOptionsBp ? 'text-red-400' : 'text-zinc-300'}`}
                  >
                    {fmtMoney(dollarTarget)}
                  </span>
                </>
              )}
            </div>
          )}
          {optMode === 'DOLLARS' && dollarTarget != null && exceedsOptionsBp && (
            <div className="text-xs text-red-400">
              Exceeds options BP{' '}
              <span className="font-mono">{fmtMoney(optionsBp)}</span>
            </div>
          )}
          {optMode !== 'CONTRACTS' && !exceedsOptionsBp && optionsBp > 0 && (
            <div className="text-xs text-zinc-500">
              Opt BP <span className="font-mono text-zinc-300">{fmtMoney(optionsBp)}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            <span>Contracts</span>
            <input
              type="number"
              min={1}
              step={1}
              value={contractsQty}
              onChange={(e) => {
                setContractsQty(e.target.value)
                clearArm()
              }}
              className="w-16 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 font-mono text-sm text-center focus:outline-none focus:border-emerald-500"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            <span>Strikes ±</span>
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              value={strikesEach}
              onChange={(e) => {
                const n = Number(e.target.value)
                if (Number.isFinite(n) && n >= 1 && n <= 50) setStrikesEach(Math.round(n))
              }}
              className="w-16 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 font-mono text-sm text-center focus:outline-none focus:border-emerald-500"
            />
          </label>
        </>
      )}

      <div className={dense ? 'flex items-center gap-3' : 'ml-auto flex items-center gap-3'}>
        {chainQ.isFetching && expiration && (
          <span className="text-xs text-zinc-500">refreshing…</span>
        )}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className="text-xs text-zinc-300">Show chain</span>
          <span
            className={`relative w-9 h-5 rounded-full transition ${
              showChain ? 'bg-emerald-600' : 'bg-zinc-700'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                showChain ? 'translate-x-4' : ''
              }`}
            />
          </span>
          <input
            type="checkbox"
            checked={showChain}
            onChange={(e) => {
              setShowChain(e.target.checked)
              clearArm()
            }}
            className="sr-only"
          />
        </label>
      </div>
    </>
  )

  return (
    <div className={dense ? 'flex h-full flex-col' : undefined}>
      {expQ.isLoading && <div className="p-6 text-zinc-400 text-sm">Loading expirations…</div>}

      {expQ.error && (
        <div className="p-4 text-red-400 text-sm whitespace-pre-wrap break-all">
          {(expQ.error as Error).message}
        </div>
      )}

      {noOptions && (
        <div className="p-6 text-center text-zinc-500 text-sm">
          No options listed for {symbol}.
        </div>
      )}

      {expirations.length > 0 && (
        <>
          {dense ? (
            <div ref={controlsRef} className="relative shrink-0 border-b border-zinc-800">
              <button
                type="button"
                onClick={() => setControlsOpen((o) => !o)}
                aria-label="Order settings"
                className="flex w-full items-center justify-between gap-2 px-3 h-9 text-xs text-zinc-300 hover:bg-white/[0.03] transition"
              >
                <span className="truncate font-mono">{triggerSummary}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0 text-zinc-400">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
              {controlsOpen && (
                <div className="absolute right-2 top-full z-30 mt-1 flex w-[270px] flex-col gap-3 rounded-lg border border-zinc-700 bg-zinc-950 p-3 shadow-xl">
                  {controlsInner}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 px-3 py-3 sm:p-4 border-b border-zinc-800">
              {controlsInner}
            </div>
          )}

          {!quickEnabled && armedKey && (
            <div className="px-4 pt-2 text-xs text-zinc-500">
              Click the same cell again within {ARM_TIMEOUT_MS / 1000}s to confirm, or wait to cancel.
            </div>
          )}

          {callBuyBlockedMessage && callBuyBlockedMessage === putBuyBlockedMessage && (
            <div className="mx-4 mt-3 px-3 py-2 rounded border border-amber-700/60 bg-amber-950/30 text-xs text-amber-300">
              {callBuyBlockedMessage}
            </div>
          )}
          {callBuyBlockedMessage && callBuyBlockedMessage !== putBuyBlockedMessage && (
            <div className="mx-4 mt-3 px-3 py-2 rounded border border-amber-700/60 bg-amber-950/30 text-xs text-amber-300">
              Calls: {callBuyBlockedMessage}
            </div>
          )}
          {putBuyBlockedMessage && putBuyBlockedMessage !== callBuyBlockedMessage && (
            <div className="mx-4 mt-2 px-3 py-2 rounded border border-amber-700/60 bg-amber-950/30 text-xs text-amber-300">
              Puts: {putBuyBlockedMessage}
            </div>
          )}

          {chainQ.error && (
            <div className="p-4 text-red-400 text-sm whitespace-pre-wrap break-all">
              {(chainQ.error as Error).message}
            </div>
          )}
          {chainQ.data && showChain && (
            <OptionsChainTable
              rows={chainRows}
              atmStrike={atmStrike}
              armedKey={armedKey}
              submittingKey={submittingKey}
              onCellClick={handleCellClick}
              callBuyDisabledReason={callBuyBlockedMessage}
              putBuyDisabledReason={putBuyBlockedMessage}
            />
          )}
          {!showChain && (
            <SimpleTradeButtons
              dense={dense}
              symbol={symbol}
              underlying={underlying}
              atmStrike={atmStrike}
              expiration={expiration}
              call={atmContracts.call}
              put={atmContracts.put}
              armedKey={armedKey}
              submittingKey={submittingKey}
              quickEnabled={quickEnabled}
              onClick={handleCellClick}
              resolve={(c) => resolveContractsFor(c, 'BUY')}
              callBuyDisabledReason={callBuyBlockedMessage}
              putBuyDisabledReason={putBuyBlockedMessage}
              isFleetMode={isFleetMode}
              accounts={accounts}
              upPreflightData={upPreflightQuery.data ?? null}
              downPreflightData={downPreflightQuery.data ?? null}
              upReferencePrice={upFleetPayload?.referencePrice ?? 0}
              downReferencePrice={downFleetPayload?.referencePrice ?? 0}
              upFleetPending={upFleetMutation.isPending}
              downFleetPending={downFleetMutation.isPending}
              upStatusResult={lastUpResult}
              downStatusResult={lastDownResult}
              onDismissUp={() => setLastUpResult(null)}
              onDismissDown={() => setLastDownResult(null)}
            />
          )}
        </>
      )}
    </div>
  )
}

interface SimpleTradeButtonsProps {
  dense?: boolean
  symbol: string
  underlying: number | null
  atmStrike: number | null
  expiration: string | null
  call: OptionChainContract | null
  put: OptionChainContract | null
  armedKey: string | null
  submittingKey: string | null
  quickEnabled: boolean
  onClick: (side: OptionSide, action: 'BUY' | 'SELL', contract: OptionChainContract) => void
  resolve: (
    c: OptionChainContract,
  ) => { contracts: number; costUSD: number | null } | { error: string }
  /** When non-null, the CALL BUY button renders disabled with this string as its tooltip. */
  callBuyDisabledReason: string | null
  /** When non-null, the PUT BUY button renders disabled with this string as its tooltip. */
  putBuyDisabledReason: string | null
  // Fleet props
  isFleetMode: boolean
  accounts: AccountSummary[]
  upPreflightData: PreflightResponse | null
  downPreflightData: PreflightResponse | null
  upReferencePrice: number
  downReferencePrice: number
  upFleetPending: boolean
  downFleetPending: boolean
  upStatusResult: FleetSubmitResult | null
  downStatusResult: FleetSubmitResult | null
  onDismissUp: () => void
  onDismissDown: () => void
}

function SimpleTradeButtons({
  dense = false,
  symbol,
  underlying,
  atmStrike,
  expiration,
  call,
  put,
  armedKey,
  submittingKey,
  quickEnabled,
  onClick,
  resolve,
  callBuyDisabledReason,
  putBuyDisabledReason,
  isFleetMode,
  accounts,
  upPreflightData,
  downPreflightData,
  upReferencePrice,
  downReferencePrice,
  upFleetPending,
  downFleetPending,
  upStatusResult,
  downStatusResult,
  onDismissUp,
  onDismissDown,
}: SimpleTradeButtonsProps) {
  const callKey = call ? `${call.instrument.symbol}:BUY` : null
  const putKey = put ? `${put.instrument.symbol}:BUY` : null
  const callArmed = callKey != null && armedKey === callKey
  const putArmed = putKey != null && armedKey === putKey
  const callSubmitting = callKey != null && submittingKey === callKey
  const putSubmitting = putKey != null && submittingKey === putKey
  const anySubmitting = submittingKey != null

  const callResolved = call ? resolve(call) : null
  const putResolved = put ? resolve(put) : null
  const callError = callResolved && 'error' in callResolved ? callResolved.error : null
  const putError = putResolved && 'error' in putResolved ? putResolved.error : null
  const callOK = callResolved && 'contracts' in callResolved ? callResolved : null
  const putOK = putResolved && 'contracts' in putResolved ? putResolved : null

  function detailLine(
    contract: OptionChainContract | null,
    ok: { contracts: number; costUSD: number | null } | null,
    err: string | null,
  ): string {
    if (!contract) return 'No ATM contract'
    if (err) return err
    if (!ok) return `ask ${contract.ask ?? '—'}`
    const cost = ok.costUSD != null ? ` ≈ ${fmtMoney(ok.costUSD)}` : ''
    return `${ok.contracts}× @ ${contract.ask ?? '—'}${cost}`
  }

  return (
    <div className={dense ? 'flex min-h-0 flex-1 flex-col' : 'p-6 space-y-4'}>
      {!dense && (
      <div className="text-center text-sm text-zinc-400">
        <span className="text-zinc-200 font-semibold">{symbol}</span>
        <span className="mx-2 text-zinc-600">·</span>
        <span className="font-mono">
          {underlying != null ? `$${underlying.toFixed(2)}` : '—'}
        </span>
        <span className="mx-2 text-zinc-600">·</span>
        <span>ATM {atmStrike != null ? atmStrike.toFixed(2) : '—'}</span>
        <span className="mx-2 text-zinc-600">·</span>
        <span>Exp {expiration ? formatExpiration(expiration) : '—'}</span>
      </div>
      )}
      <div className={dense ? 'flex min-h-0 flex-1 flex-col' : 'grid grid-cols-2 gap-4'}>
        <button
          type="button"
          disabled={!call || anySubmitting || (isFleetMode && upFleetPending) || callError != null || callBuyDisabledReason != null}
          onClick={() => call && onClick('CALL', 'BUY', call)}
          title={callBuyDisabledReason ?? callError ?? undefined}
          className={`flex flex-col items-center justify-center gap-1 font-semibold transition ${
            dense ? 'min-h-0 flex-1 py-2.5' : 'py-8 rounded-lg border-2'
          } ${
            callArmed
              ? 'bg-emerald-600 border-emerald-400 text-white'
              : 'bg-emerald-950/40 border-emerald-800 text-emerald-300 hover:bg-emerald-900/50 hover:border-emerald-600'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <span className={`leading-none ${dense ? 'text-2xl' : 'text-3xl'}`}>↑</span>
          <span className={dense ? 'text-sm' : 'text-base'}>
            {(callSubmitting || (isFleetMode && upFleetPending))
              ? 'Submitting…'
              : isFleetMode
                ? 'Up — Buy Call'
                : `Up — Buy ${callOK ? `${callOK.contracts}× ` : ''}Call`}
          </span>
          <span
            className={`text-xs font-mono ${callError ? 'text-red-400' : 'text-emerald-400/70'}`}
          >
            {call
              ? isFleetMode
                ? `${atmStrike?.toFixed(2)} · ask ${call.ask ?? '—'}`
                : `${atmStrike?.toFixed(2)} · ${detailLine(call, callOK, callError)}`
              : 'No ATM call'}
          </span>
        </button>
        <button
          type="button"
          disabled={!put || anySubmitting || (isFleetMode && downFleetPending) || putError != null || putBuyDisabledReason != null}
          onClick={() => put && onClick('PUT', 'BUY', put)}
          title={putBuyDisabledReason ?? putError ?? undefined}
          className={`flex flex-col items-center justify-center gap-1 font-semibold transition ${
            dense ? 'min-h-0 flex-1 py-2.5' : 'py-8 rounded-lg border-2'
          } ${
            putArmed
              ? 'bg-red-600 border-red-400 text-white'
              : 'bg-red-950/40 border-red-800 text-red-300 hover:bg-red-900/50 hover:border-red-600'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <span className={`leading-none ${dense ? 'text-2xl' : 'text-3xl'}`}>↓</span>
          <span className={dense ? 'text-sm' : 'text-base'}>
            {(putSubmitting || (isFleetMode && downFleetPending))
              ? 'Submitting…'
              : isFleetMode
                ? 'Down — Buy Put'
                : `Down — Buy ${putOK ? `${putOK.contracts}× ` : ''}Put`}
          </span>
          <span
            className={`text-xs font-mono ${putError ? 'text-red-400' : 'text-red-400/70'}`}
          >
            {put
              ? isFleetMode
                ? `${atmStrike?.toFixed(2)} · ask ${put.ask ?? '—'}`
                : `${atmStrike?.toFixed(2)} · ${detailLine(put, putOK, putError)}`
              : 'No ATM put'}
          </span>
        </button>
      </div>
      {isFleetMode && (upPreflightData || downPreflightData || upStatusResult || downStatusResult) && (
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-zinc-800">
          <div>
            {upStatusResult ? (
              <StatusGrid
                result={upStatusResult}
                accounts={accounts}
                onDismiss={onDismissUp}
              />
            ) : upPreflightData ? (
              <div>
                <div className="flex items-center justify-between text-[11px] text-emerald-300/80 py-2">
                  <span className="uppercase tracking-wider text-zinc-500">Up preflight</span>
                  <span className="font-mono">
                    {upPreflightData.rows.filter((r) => r.status !== 'skip').length} of {upPreflightData.armedCount} · ~${
                      upPreflightData.rows.filter((r) => r.status !== 'skip').reduce((s, r) => s + r.estCost, 0).toFixed(0)
                    }
                  </span>
                </div>
                <PreflightGrid
                  rows={upPreflightData.rows}
                  accounts={accounts}
                  referencePrice={upReferencePrice}
                  variant="compact"
                />
              </div>
            ) : null}
          </div>
          <div>
            {downStatusResult ? (
              <StatusGrid
                result={downStatusResult}
                accounts={accounts}
                onDismiss={onDismissDown}
              />
            ) : downPreflightData ? (
              <div>
                <div className="flex items-center justify-between text-[11px] text-rose-300/80 py-2">
                  <span className="uppercase tracking-wider text-zinc-500">Down preflight</span>
                  <span className="font-mono">
                    {downPreflightData.rows.filter((r) => r.status !== 'skip').length} of {downPreflightData.armedCount} · ~${
                      downPreflightData.rows.filter((r) => r.status !== 'skip').reduce((s, r) => s + r.estCost, 0).toFixed(0)
                    }
                  </span>
                </div>
                <PreflightGrid
                  rows={downPreflightData.rows}
                  accounts={accounts}
                  referencePrice={downReferencePrice}
                  variant="compact"
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
      {!quickEnabled && (callArmed || putArmed) && (
        <div className="text-center text-xs text-zinc-500">
          Click again to confirm.
        </div>
      )}
    </div>
  )
}
